/**
 * THE SHOP'S FLOATING DOCK (DK parity, S1d).
 *
 * Mounted once in App.js, beside the cart bar. Today it holds one thing, the
 * "צריכים עזרה?" WhatsApp button; the visitor accessibility menu joins it later
 * (S3b-3). It renders NOTHING unless all of these hold, which is what keeps
 * every shop that never asked exactly as it was:
 *
 *   - the owner turned the button on (`floatingWhatsappEnabled === true` —
 *     a stored word is not a yes);
 *   - the shop has a number WhatsApp can open (`shopChannels`, the one answer
 *     the hero and the side panel read; deliberately not `heroShowWhatsapp`,
 *     which is the hero's own switch);
 *   - the address is any page of this business the shopper browses
 *     (utils/shopSurface's `isCustomerSurface`) — its main page included — and
 *     not a checkout, a placed order or a booking confirmation, which own the
 *     bottom of the screen;
 *   - and on stylon.online, the address is under THIS shop's slug.
 *
 * It floats above the cart bar by reading the bar's own published height, and
 * publishes its own (`--shop-dock-clearance`) so the end of the footer can
 * leave room for it.
 */
import React, { useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isDomainMode } from '../../utils/domainMode';
import { isCustomerSurface, shopSlugOfPath } from '../../utils/shopSurface';
import { useCurrentProductName, useCurrentShop } from '../../utils/currentShop';
import { shopChannels } from '../../Pages/Shop/ShopActionButtons';
import usePublishedHeight from '../../hooks/usePublishedHeight';
import FloatingWhatsappButton from './FloatingWhatsappButton';
import styles from './shopFloatingDock.module.css';

const RTL = new Set(['he', 'ar']);
const DOCK_GAP_PX = 16;

function Dock({ children, dir }) {
  const ref = useRef(null);
  usePublishedHeight(ref, { name: '--shop-dock-clearance', key: 'floating-dock', gap: DOCK_GAP_PX });
  return (
    <div ref={ref} className={styles.dock} dir={dir} data-testid="shop-floating-dock">
      {children}
    </div>
  );
}

export default function ShopFloatingDock() {
  const { pathname } = useLocation();
  const salon = useCurrentShop();
  const productName = useCurrentProductName();
  const { t, i18n } = useTranslation();

  if (!salon || salon.floatingWhatsappEnabled !== true) return null;
  const domainMode = isDomainMode();
  // EVERY page of this business, not only its shop. The dock was built for the
  // shop pages and read `isShopSurface`, so a business that switched it on still
  // saw nothing on the page most of its customers land on (owner, 2026-10-03:
  // "in both I can't see it! in the customer side?! Where is it floating!").
  // `isCustomerSurface` excludes only the pages that own the bottom of the
  // screen — a checkout, a placed order, a booking confirmation.
  if (!isCustomerSurface(pathname, domainMode)) return null;
  if (!domainMode && shopSlugOfPath(pathname) !== (salon.slug ?? salon.username)) return null;
  const { whatsappUrl } = shopChannels(salon);
  if (!whatsappUrl) return null;

  const onProduct = /(^|\/)p\/[^/]+\/?$/.test(pathname);
  const lang = String(i18n?.language || '').split('-')[0];
  return (
    <Dock dir={RTL.has(lang) ? 'rtl' : 'ltr'}>
      <FloatingWhatsappButton
        whatsappUrl={whatsappUrl}
        shopName={salon.name}
        productName={onProduct ? productName : null}
        t={t}
      />
    </Dock>
  );
}
